import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * Money model:
 *  - All prices are stored in USD on the backend (source of truth).
 *  - Frontend converts to display currency using USD_TO_INR (from env, fallback 83).
 *  - Default: INR for users detected as Indian (locale/timezone), USD otherwise.
 *  - User can flip via header dropdown; choice persists in localStorage.
 */

const USD_TO_INR = Number(process.env.REACT_APP_USD_TO_INR) || 83;
export const CURRENCIES = {
  USD: { code: "USD", symbol: "$", rate: 1, locale: "en-US" },
  INR: { code: "INR", symbol: "₹", rate: USD_TO_INR, locale: "en-IN" },
};

const CurrencyContext = createContext(null);

function detectDefault() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = (navigator.language || navigator.userLanguage || "").toLowerCase();
    if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta" || lang.endsWith("-in") || lang === "hi" || lang.startsWith("hi-")) {
      return "INR";
    }
  } catch {}
  return "USD";
}

export function CurrencyProvider({ children }) {
  const [code, setCode] = useState(() => localStorage.getItem("noxi_currency") || detectDefault());

  useEffect(() => { localStorage.setItem("noxi_currency", code); }, [code]);

  const cfg = CURRENCIES[code] || CURRENCIES.USD;

  const format = useCallback((usd, { withSymbol = true, decimals } = {}) => {
    const n = Number(usd) || 0;
    const converted = n * cfg.rate;
    const d = decimals != null ? decimals : (cfg.code === "INR" ? 0 : 2);
    const formatted = converted.toLocaleString(cfg.locale, {
      minimumFractionDigits: d, maximumFractionDigits: d,
    });
    return withSymbol ? `${cfg.symbol}${formatted}` : formatted;
  }, [cfg]);

  const value = useMemo(() => ({
    code, symbol: cfg.symbol, rate: cfg.rate,
    setCurrency: (c) => setCode(CURRENCIES[c] ? c : "USD"),
    format,
  }), [code, cfg, format]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // graceful fallback for non-wrapped components
    return { code: "USD", symbol: "$", rate: 1, setCurrency: () => {}, format: (usd) => `$${(Number(usd) || 0).toFixed(2)}` };
  }
  return ctx;
};
